(function () {
  /**
   * Shared UI primitives for the eros-commerce-* modules (auth, checkout, account, orders).
   *
   * Deliberately built on the native <dialog> element rather than the theme's own
   * `drawer`/`dialog-*` custom elements: those are defined inside the vendor's
   * pre-bundled, minified assets/theme.js with no readable source in this repo, so
   * extending them safely isn't possible. Visual consistency instead comes from
   * reusing the theme's CSS custom properties (snippets/css-variables.liquid) —
   * --accent, --button-background, --text-color, .color-scheme--* — not from
   * sharing markup/JS with the vendor components.
   */

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      for (var key in attrs) {
        // `disabled: cond ? 'disabled' : undefined` is a common pattern at
        // call sites — setAttribute(key, undefined) would stringify to the
        // literal "undefined", which still counts as present for boolean
        // attributes like `disabled` and permanently disables the element
        // regardless of `cond`. Skip null/undefined instead of setting them.
        if (Object.prototype.hasOwnProperty.call(attrs, key) && attrs[key] !== undefined && attrs[key] !== null) {
          node.setAttribute(key, attrs[key]);
        }
      }
    }
    return node;
  }

  // --- Sheet (bottom sheet on mobile widths, centered dialog above) ---

  function openSheet(options) {
    options = options || {};
    // .color-scheme--dialog is the theme's own modal/dialog color scheme
    // (snippets/css-variables.liquid, driven by settings.modal_color_scheme) —
    // reused here instead of a bespoke scheme so sheets match native dialogs.
    var dialog = el('dialog', 'eros-sheet color-scheme color-scheme--dialog');
    var header = el('div', 'eros-sheet__header');
    var title = el('p', 'h4 eros-sheet__title');
    title.textContent = options.title || '';
    var closeBtn = el('button', 'eros-sheet__close', { type: 'button', 'aria-label': options.closeLabel || 'Close' });
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', function () {
      close();
    });
    header.appendChild(title);
    header.appendChild(closeBtn);

    var body = el('div', 'eros-sheet__body');
    if (options.content instanceof Node) {
      body.appendChild(options.content);
    }

    dialog.appendChild(header);
    dialog.appendChild(body);
    document.body.appendChild(dialog);

    function close() {
      dialog.close();
    }

    dialog.addEventListener('close', function () {
      dialog.remove();
      if (typeof options.onClose === 'function') options.onClose();
    });

    // Clicking the ::backdrop area (outside the dialog box) closes it.
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close();
    });

    dialog.showModal();

    return {
      dialog: dialog,
      body: body,
      close: close,
    };
  }

  // --- Toast ---
  // Ports mobile_app's ErosTransientFeedback behavior, not just its look:
  // - a new toast replaces the current one immediately, never stacks
  //   (ErosTransientFeedbackHost calls hideCurrentSnackBar() before
  //   showSnackBar() on every call — eros_transient_feedback_host.dart)
  // - identical (message, kind) calls within 2s are deduped/ignored
  //   (ErosTransientFeedbackStyle.dedupeWindow — eros_transient_feedback_style.dart)
  // - 3000ms auto-dismiss (defaultDuration), swipe-down dismisses early
  //   (dismissDirection: DismissDirection.down)

  var TOAST_DURATION_MS = 3000;
  var TOAST_EXIT_MS = 250;
  var TOAST_DEDUPE_MS = 2000;
  var TOAST_SWIPE_THRESHOLD_PX = 40;

  var toastHost = null;
  var activeToastNode = null;
  var activeToastTimer = null;
  var lastToastKey = null;
  var lastToastAt = 0;

  function ensureToastHost() {
    if (!toastHost) {
      toastHost = el('div', 'eros-toast-host', { role: 'status', 'aria-live': 'polite' });
    }
    // A modal <dialog> sits in the browser's top layer, above any z-index —
    // a body-level toast was invisible behind the auth/checkout/cancel
    // sheets (audit 2026-09-13). Host the toast inside the topmost open
    // dialog while one is up, back on <body> otherwise.
    var dialogs = document.querySelectorAll('dialog[open]');
    var parent = dialogs.length ? dialogs[dialogs.length - 1] : document.body;
    if (toastHost.parentNode !== parent) parent.appendChild(toastHost);
    return toastHost;
  }

  function removeActiveToast() {
    if (activeToastTimer) {
      clearTimeout(activeToastTimer);
      activeToastTimer = null;
    }
    var node = activeToastNode;
    activeToastNode = null;
    if (!node) return;
    node.classList.remove('eros-toast--visible');
    setTimeout(function () {
      node.remove();
    }, TOAST_EXIT_MS);
  }

  function bindSwipeToDismiss(node) {
    var startY = null;
    node.addEventListener(
      'touchstart',
      function (event) {
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );
    node.addEventListener(
      'touchmove',
      function (event) {
        if (startY === null) return;
        var dy = event.touches[0].clientY - startY;
        if (dy > TOAST_SWIPE_THRESHOLD_PX) {
          startY = null;
          removeActiveToast();
        }
      },
      { passive: true }
    );
    node.addEventListener('touchend', function () {
      startY = null;
    });
  }

  /** `link` ({href, label}) adds one tappable action after the text (ADR-301: «تواصل معنا»). */
  function toast(message, kind, link) {
    var key = (kind || 'info') + ':' + message;
    var now = Date.now();
    if (key === lastToastKey && now - lastToastAt < TOAST_DEDUPE_MS) return;
    lastToastKey = key;
    lastToastAt = now;

    removeActiveToast();

    var host = ensureToastHost();
    host.style.pointerEvents = 'none';
    var node = el('div', 'eros-toast eros-toast--' + (kind || 'info'));
    node.textContent = message;
    if (link && link.href) {
      var a = el('a', 'eros-toast__action');
      a.href = link.href;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = link.label;
      node.appendChild(document.createTextNode(' '));
      node.appendChild(a);
    }
    node.style.pointerEvents = 'auto';
    host.appendChild(node);
    activeToastNode = node;
    bindSwipeToDismiss(node);
    // Force a reflow so the enter transition plays.
    void node.offsetHeight;
    node.classList.add('eros-toast--visible');
    activeToastTimer = setTimeout(function () {
      activeToastTimer = null;
      removeActiveToast();
    }, TOAST_DURATION_MS);
  }

  // --- Form error helper ---

  function setFieldError(fieldEl, message) {
    var wrapper = fieldEl.closest('.form-control') || fieldEl.parentElement;
    var existing = wrapper.querySelector('.eros-field__error');
    if (message) {
      if (!existing) {
        existing = el('p', 'eros-field__error');
        wrapper.appendChild(existing);
      }
      existing.textContent = message;
      fieldEl.setAttribute('aria-invalid', 'true');
    } else if (existing) {
      existing.remove();
      fieldEl.removeAttribute('aria-invalid');
    }
  }

  // --- OTP code input (ADR-320): ONE real input under N display-only boxes ---
  //
  // - The input carries autocomplete="one-time-code" (SMS autofill fills it whole), a numeric keyboard and
  //   one label for screen readers; the boxes are aria-hidden and only show its first N characters.
  // - Its caret always sits at the end, so whichever box is touched the next digit lands in the first empty
  //   box; typing appends, backspace removes the last digit.
  // - ٠-٩ and ۰-۹ become 0-9 and anything else (spaces, dashes) is dropped, before display and before sending.
  // - opts.maxPasteLength (ADR-208): a pasted code longer than `length` (the staff master code, up to this
  //   many digits) is submitted whole. opts.label, when given, names the field for screen readers.

  /** Arabic-Indic and Persian digits to 0-9, everything that is not a digit dropped (mirrors the server). */
  function westernDigits(text) {
    return String(text == null ? '' : text)
      .replace(/[\u0660-\u0669]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
      .replace(/[\u06f0-\u06f9]/g, function (d) { return String(d.charCodeAt(0) - 0x06f0); })
      .replace(/[^0-9]/g, '');
  }

  function buildOtpInput(container, length, onComplete, opts) {
    length = length || 6;
    var maxPasteLength = Math.max(length, (opts && opts.maxPasteLength) || length);
    container.classList.add('eros-otp-input');
    container.setAttribute('dir', 'ltr');
    var input = el('input', 'eros-otp-input__field', {
      type: 'text',
      inputmode: 'numeric',
      autocomplete: 'one-time-code',
      maxlength: String(maxPasteLength),
    });
    if (opts && opts.label) input.setAttribute('aria-label', opts.label);
    var row = el('div', 'eros-otp-input__boxes', { 'aria-hidden': 'true' });
    var boxes = [];
    for (var i = 0; i < length; i++) {
      var box = el('span', 'eros-otp-input__box');
      boxes.push(box);
      row.appendChild(box);
    }
    container.appendChild(row);
    container.appendChild(input);
    var completed = '';

    function toEnd() {
      try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) { /* not focusable yet */ }
    }

    function render() {
      var v = input.value;
      var long = v.length > length;
      boxes.forEach(function (b, k) {
        b.textContent = long ? '•' : v.charAt(k);
        if (b.classList.toggle) b.classList.toggle('is-active', !long && k === Math.min(v.length, length - 1));
      });
    }

    function settle(whole) {
      var v = westernDigits(input.value).slice(0, maxPasteLength);
      if (!whole && v.length > length) v = v.slice(0, length);
      input.value = v;
      render();
      toEnd();
      var done = v.length === length || (whole && v.length > length);
      if (done && v !== completed && typeof onComplete === 'function') {
        completed = v;
        onComplete(v);
      }
      if (!done) completed = '';
    }

    input.addEventListener('input', function () {
      // An autofill or a paste that the browser inserted itself may carry more than the boxes.
      settle(westernDigits(input.value).length > length);
    });
    input.addEventListener('paste', function (e) {
      var text = westernDigits((e.clipboardData || window.clipboardData).getData('text'));
      if (!text) return;
      e.preventDefault();
      input.value = text;
      settle(true);
    });
    input.addEventListener('focus', toEnd);
    input.addEventListener('click', toEnd);
    input.addEventListener('keyup', toEnd);
    render();

    return {
      focus: function () {
        input.focus();
        toEnd();
      },
      clear: function () {
        input.value = '';
        completed = '';
        render();
        input.focus();
      },
    };
  }

  // --- Money formatting ---
  // Shared so every module (orders, wallet, checkout, hybrid search, cart,
  // PDP stepper) renders money the same way instead of each re-implementing
  // it slightly differently. Consolidated 2026-08-27 from four separate
  // copies (eros-cart-recently-removed.js, eros-hybrid-search.js,
  // eros-pdp-cart-stepper.js, and this file's old formatMoneyIqd).
  //
  // window.Shopify.formatMoney is never actually defined in this theme
  // (confirmed live — the platform-injected `Shopify` global doesn't carry
  // it, and nothing in theme.js/theme.liquid assigns it), so naive code
  // falls through to `(cents/100).toFixed(2)`, which on a zero-decimal
  // currency like IQD both divides wrongly and forces decimals —
  // "15,000 د.ع" would render as "150.00". Format the store's own
  // moneyFormat/moneyWithCurrencyFormat setting string directly instead
  // (window.themeVariables.settings, populated from shop.money_format in
  // snippets/js-variables.liquid).
  function moneyFormatString() {
    var settings = (window.themeVariables && window.themeVariables.settings) || {};
    return settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
  }

  // Core formatter — `amount` is already in major currency units (e.g. IQD
  // dinars, not fils/cents). The two public entry points below just convert
  // their own input convention into major units and call this.
  /**
   * Dinar mark for the page's language, mirroring Flutter's
   * `iqdSuffixForLocale` (money_formatter.dart) word for word.
   *
   * Shopify's `money_format` is ONE shop-wide string — "{{amount_no_decimals}}
   * د.ع" — so an English or Kurdish shopper saw an Arabic currency mark on
   * every price, including the add-to-cart button (owner report 2026-09-07).
   * The wording is not invented here: it copies what the locale files already
   * say for the same currency.
   */
  function iqdSuffixForLang() {
    var lang = (document.documentElement.getAttribute('lang') || 'ar')
      .split(/[-_]/)[0]
      .toLowerCase();
    if (lang === 'en') return 'IQD';
    if (lang === 'ku' || lang === 'ckb') return 'دینار';
    return 'د.ع';
  }

  function applyMoneyFormat(amount) {
    if (!isFinite(amount)) return '';
    var format = moneyFormatString() || '{{amount_no_decimals}}';
    var placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = placeholderMatch ? placeholderMatch[1] : 'amount_no_decimals';
    var noDecimals = key.indexOf('no_decimals') !== -1;
    var value = noDecimals ? Math.round(amount) : amount;
    var valueStr = noDecimals ? String(value) : value.toFixed(2);
    var intPart = valueStr.split('.')[0];
    var rest = noDecimals ? '' : '.' + valueStr.split('.')[1];
    var grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formattedAmount = grouped + rest;
    if (!placeholderMatch) return formattedAmount;

    // Keep the shop's own layout (placeholder position, spacing, any
    // punctuation) and swap ONLY the Arabic dinar mark, so a merchant edit to
    // money_format still governs everything except the language of the unit.
    return format
      .replace(placeholderMatch[0], formattedAmount)
      .replace(/د\.ع/g, iqdSuffixForLang());
  }

  // `cents` is Shopify Cart-AJAX minor units (e.g. item.final_price, cart.total_price).
  function formatMoneyCents(cents) {
    var amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    return applyMoneyFormat(amount / 100);
  }

  // `amountDecimalStr` is a decimal major-unit string/number (e.g. a
  // predictive-search card's `price`, or an Admin/GraphQL MoneyV2 `.amount`
  // string like "15000.0").
  function formatMoneyAmount(amountDecimalStr) {
    var amount = parseFloat(amountDecimalStr);
    if (!isFinite(amount)) return '';
    return applyMoneyFormat(amount);
  }

  // Deprecated alias. Every existing call site (eros-checkout.js,
  // eros-account-orders.js, eros-account-shell.js, eros-checkout-payment.js,
  // eros-quick-buy-parity.js) already passes a major-unit amount (dinars,
  // not fils), the same contract as formatMoneyAmount — confirmed by
  // grep 2026-08-27 (e.g. eros-quick-buy-parity.js explicitly does
  // `Math.round(cents / 100)` before calling this). The old body hardcoded
  // `.toLocaleString('ar') + ' د.ع'`, which ignored the shop's actual
  // money_format setting and rendered Eastern Arabic-Indic digits inconsistent
  // with every other price on the site. Delegates to formatMoneyAmount so all
  // money on the site is grouped/suffixed identically. New code should call
  // formatMoneyAmount / formatMoneyCents directly instead of this alias.
  function formatMoneyIqd(amount) {
    return formatMoneyAmount(amount);
  }

  function formatDateAr(iso) {
    try {
      return new Date(iso).toLocaleDateString('ar-IQ', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  window.ErosUI = {
    el: el,
    openSheet: openSheet,
    toast: toast,
    setFieldError: setFieldError,
    buildOtpInput: buildOtpInput,
    westernDigits: westernDigits,
    formatMoneyCents: formatMoneyCents,
    formatMoneyAmount: formatMoneyAmount,
    formatMoneyIqd: formatMoneyIqd,
    formatDateAr: formatDateAr,
  };
})();
